import { PluginKey } from "@tiptap/pm/state";
import { Extension, ReactRenderer } from "@tiptap/react";
import { Suggestion } from "@tiptap/suggestion";
import { POPOVER_Z_INDEX } from "../../../common/Popover/Popover";
import type { AttributeOption } from "../types";
import {
  AttributeSuggestionList,
  type AttributeSuggestionListProps,
  type AttributeSuggestionListRef,
} from "./AttributeSuggestionList";
import { attributeTokenContent } from "./attributeToken";

/**
 * `@` quick-insert for the attribute picker (ENG-591).
 *
 * One `@tiptap/suggestion` instance per trigger character. Typing the char at a
 * word boundary opens an inline list (`AttributeSuggestionList`); choosing an
 * item replaces the typed `@query` with an inline `attributeToken` node.
 * Keyboard: arrows move, Enter or Tab picks, Escape closes. `allowSpaces` is on
 * so Space keeps typing and multi-word labels stay filterable. The plugin owns
 * positioning (Floating UI via `props.mount`), keyboard nav wiring, and
 * outside-click dismissal.
 *
 * `/` is intentionally left free for a future command-style slash menu.
 */
export interface AttributeSuggestionOptions {
  /** Trigger characters. Each gets its own suggestion plugin. */
  chars: string[];
  /** Latest attribute list, read fresh on every keystroke. */
  getItems: () => AttributeOption[];
  /** Cap on rows fed to the list. The list itself scrolls with the arrow keys. */
  limit: number;
}

/**
 * Token match, so a spaced query still narrows: every whitespace-separated word
 * of the query must appear in the label or group. Whole-string prefix ranks
 * best, then whole-string substring, then a plain token match.
 */
const rank = (items: AttributeOption[], query: string, limit: number): AttributeOption[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return items.slice(0, limit);
  const words = needle.split(/\s+/);

  const scored: Array<{ item: AttributeOption; score: number }> = [];
  for (const item of items) {
    const label = item.label.toLowerCase();
    const haystack = `${label} ${item.group.toLowerCase()}`;
    if (!words.every((word) => haystack.includes(word))) continue;

    let score = 3;
    if (label.startsWith(needle)) score = 0;
    else if (label.includes(needle)) score = 1;
    else if (words.every((word) => label.includes(word))) score = 2;
    scored.push({ item, score });
  }
  scored.sort((a, b) => a.score - b.score);
  return scored.slice(0, limit).map((entry) => entry.item);
};

let listCounter = 0;

/** ARIA combobox wiring on the editor, which keeps focus while the list is open. */
const setEditorAria = (dom: HTMLElement, listId: string | undefined, activeId?: string) => {
  const attributes: Record<string, string | undefined> = {
    "aria-haspopup": listId ? "listbox" : undefined,
    "aria-expanded": listId ? "true" : undefined,
    "aria-controls": listId,
    "aria-activedescendant": activeId,
  };
  for (const [name, value] of Object.entries(attributes)) {
    if (value === undefined) dom.removeAttribute(name);
    else dom.setAttribute(name, value);
  }
};

export const AttributeSuggestion = Extension.create<AttributeSuggestionOptions>({
  name: "attributeSuggestion",
  // Above StarterKit and the other keymaps, so an open list gets Enter and Tab
  // before they split the paragraph, indent, or (in a single-line editor) are swallowed.
  priority: 1000,

  addOptions() {
    return {
      chars: ["@"],
      getItems: () => [],
      limit: 50,
    };
  },

  addProseMirrorPlugins() {
    const { chars, getItems, limit } = this.options;
    const editor = this.editor;

    return chars.map((char) =>
      Suggestion<AttributeOption, AttributeOption>({
        editor,
        char,
        pluginKey: new PluginKey(`attributeSuggestion-${char}`),
        // Attribute labels are multi-word, so keep the menu open across spaces.
        // It closes on Escape, on selection, or when the caret leaves the range.
        allowSpaces: true,
        items: ({ query }) => rank(getItems(), query, limit),
        command: ({ editor: instance, range, props }) => {
          instance.chain().focus().deleteRange(range).insertContent(attributeTokenContent(props.key)).run();
        },
        render: () => {
          let component: ReactRenderer<AttributeSuggestionListRef, AttributeSuggestionListProps> | null = null;
          let unmount: (() => void) | null = null;

          const listId = `deci-attribute-suggestions-${++listCounter}`;
          const dom = editor.view.dom as HTMLElement;
          const onActiveIdChange = (activeId: string | undefined) => setEditorAria(dom, listId, activeId);

          return {
            onStart: (props) => {
              component = new ReactRenderer(AttributeSuggestionList, {
                editor: props.editor,
                props: { items: props.items, command: props.command, query: props.query, listId, onActiveIdChange },
              });
              // Rendered outside the app tree, so lift it above dialogs and popovers explicitly.
              component.element.style.zIndex = String(POPOVER_Z_INDEX);
              unmount = props.mount(component.element);
              setEditorAria(dom, listId);
            },
            onUpdate: (props) => {
              component?.updateProps({
                items: props.items,
                command: props.command,
                query: props.query,
                listId,
                onActiveIdChange,
              });
            },
            // Escape is handled by the plugin itself: it calls this, then exits
            // (which fires onExit below), so no teardown is needed here.
            onKeyDown: (props) => component?.ref?.onKeyDown?.(props) ?? false,
            onExit: () => {
              setEditorAria(dom, undefined);
              unmount?.();
              unmount = null;
              component?.destroy();
              component = null;
            },
          };
        },
      }),
    );
  },
});
